/* eslint-disable import/no-cycle */

import React, { createContext } from 'react';
import PropTypes from 'prop-types';

import { useContext } from '@hooks';
import { mergeClasses } from '@utils';

import { LinksContext } from './Links';

export const LinksLinkContext = createContext();

/**
 * Links.Link component that represents a link within a `Links` component.
 * Must be used as a child of a `Links` component.
 *
 * @component Links.Link
 * @param {Object} props - The properties for the Links.Link component.
 * @param {React.ReactNode} props.children - The content of the link, can include text or `Links.Link.Icon` component.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the link.
 * @param {string} props.href - The URL the link points to.
 * @param {Object} [restProps] - Additional properties to be passed to the link element.
 * @returns {ReactElement|null} The rendered Links.Link component, or null if no children are provided.
 * @example
 * <Links>
 *   <Links.Heading>Section Title</Links.Heading>
 *   <Links.Link href="/example1">Example 1</Links.Link>
 *   <Links.Link href="/example2">Example 2</Links.Link>
 * </Links>
 */
export default function LinksLink({ children, className, href, ...restProps }) {
  useContext(LinksContext, 'Links.Link must be a child of Links.');

  if (!children) return null;

  return (
    <LinksLinkContext.Provider value>
      <a
        className={mergeClasses(
          'group flex items-center gap-x-8 break-all border-0 border-b border-solid border-[#D8D8D8] text-[34px] font-extrabold uppercase tracking-[-1px] text-black no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-4 md:text-[68px]',
          className
        )}
        href={href}
        {...restProps}
      >
        {children}
      </a>
    </LinksLinkContext.Provider>
  );
}

LinksLink.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  href: PropTypes.string.isRequired
};

LinksLink.defaultProps = {
  className: ''
};
